import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LuImage,
  LuVideo,
  LuVolume2,
  LuUser,
  LuWallet,
  LuClock,
  LuCheck,
  LuPlay,
  LuPause,
  LuX,
  LuEye,
} from "react-icons/lu";
import i18n from "i18next";
import { getToken, getOrganizationId } from "../../../common/environment_config.es6";
import { approveJob } from "../../../common/server_utils.es6";

const formatDateTime = (iso, locale) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Every "veo*" id runs Gemini Omni since 2026-10-23 — show it as "Omni".
const displayModel = (model) => (/^veo(-|$)/i.test(model || "") ? "Omni" : model);

const truncate = (text, max = 60) => {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

const localizeName = (name) => {
  if (!name) return "";
  const translated = i18n.t(name);
  if (translated && translated !== name) return translated;
  const sceneMatch = name.match(/^Scene\s+(.+)$/i);
  if (sceneMatch) {
    const prefix = i18n.t("ImageHistory.Scene") || "Sahne";
    return `${prefix} ${sceneMatch[1]}`;
  }
  return name;
};

const extractPrompt = (job, mediaType, locale) => {
  if (!job) return "";
  const meta = job.metadata || {};
  if (mediaType === "video") {
    if (locale && locale.startsWith("en")) {
      return meta.videoPrompt || meta.videoPromptDisplay || job.prompt || "";
    }
    return meta.videoPromptDisplay || meta.videoPrompt || job.prompt || "";
  }
  return job.prompt || "";
};

const PROMPT_LIMITS = {
  video: 220,
  avatar: 120,
  audio: 120,
  image: 160,
};

const MEDIA_PRESETS = {
  image: {
    jobTypes: ["text-to-image", "image-editing"],
    fallbackIcon: LuImage,
    defaultNameKey: "ImageHistory.DefaultNameImage",
    defaultName: "AI Generated Image",
    spendingLabelKey: "ImageHistory.TrainingSpending",
    spendingLabelFallback: "Görsel Harcaması",
    emptyLabelKey: "ImageHistory.Empty",
    emptyLabelFallback: "Henüz görsel oluşturulmamış",
    promptLabelKey: "ImageHistory.Prompt",
    promptLabelFallback: "Prompt",
    categoryLabelKey: "ImageHistory.CategoryImage",
    categoryLabelFallback: "Görsel",
  },
  video: {
    jobTypes: ["staged-video-generation"],
    fallbackIcon: LuVideo,
    defaultNameKey: "ImageHistory.DefaultNameVideo",
    defaultName: "AI Generated Video",
    spendingLabelKey: "ImageHistory.TrainingSpending",
    spendingLabelFallback: "Toplam Harcama",
    emptyLabelKey: "ImageHistory.EmptyVideo",
    emptyLabelFallback: "Henüz video oluşturulmamış",
    promptLabelKey: "ImageHistory.Prompt",
    promptLabelFallback: "Prompt",
    categoryLabelKey: "ImageHistory.CategoryVideo",
    categoryLabelFallback: "Video",
  },
  audio: {
    jobTypes: ["text-to-speech"],
    fallbackIcon: LuVolume2,
    defaultNameKey: "ImageHistory.DefaultNameNarration",
    defaultName: "AI Generated Narration",
    spendingLabelKey: "ImageHistory.TrainingAudioSpending",
    spendingLabelFallback: "Ses Harcaması",
    emptyLabelKey: "ImageHistory.EmptyAudio",
    emptyLabelFallback: "Henüz ses oluşturulmamış",
    promptLabelKey: "ImageHistory.Prompt",
    promptLabelFallback: "Prompt",
    categoryLabelKey: "ImageHistory.CategoryAudio",
    categoryLabelFallback: "Seslendirme",
  },
  avatar: {
    jobTypes: [
      "image-to-avatar",
      "tts-avatar",
      "tavus-video",
      "lipsync",
      "ai-avatar",
      "talking-avatar",
    ],
    fallbackIcon: LuUser,
    defaultNameKey: "ImageHistory.DefaultNameAvatar",
    defaultName: "AI Generated Avatar",
    spendingLabelKey: "ImageHistory.TrainingAvatarSpending",
    spendingLabelFallback: "Avatar Harcaması",
    emptyLabelKey: "ImageHistory.EmptyAvatar",
    emptyLabelFallback: "Henüz avatar oluşturulmamış",
    promptLabelKey: "ImageHistory.Prompt",
    promptLabelFallback: "Prompt",
    categoryLabelKey: "ImageHistory.CategoryAvatar",
    categoryLabelFallback: "Avatar",
  },
};

const resolveDefaultName = (preset) => {
  const translated = i18n.t(preset.defaultNameKey);
  return translated && translated !== preset.defaultNameKey ? translated : preset.defaultName;
};

// Talking-avatar rows re-apply themselves from the job row (the host reads the
// result from job.metadata.result): their resultUrl column is null when the
// video URL is longer than 255 characters, so they must not depend on it.
const isTalkingAvatarRow = (job) => !!job && job.type === "talking-avatar";

const isRowCompleted = (job) =>
  !!job &&
  (job.status === "completed" || job.status === "inspect" || job.metadata?.status === "completed");

// resultUrl, else (talking avatar) the result video kept in the row's metadata.
const rowResultUrl = (job) =>
  (job && job.resultUrl) ||
  (isTalkingAvatarRow(job)
    ? job.metadata?.result?.videoUrl || job.metadata?.videoUrl || null
    : null);

const canAddRow = (job) => !!rowResultUrl(job) || (isTalkingAvatarRow(job) && isRowCompleted(job));

const addFailedText = () =>
  i18n.t("ImageHistory.AddFailed", {
    defaultValue: "Sahneye eklenemedi; lütfen tekrar deneyin.",
  }) || "Sahneye eklenemedi; lütfen tekrar deneyin.";

// Bu job tipleri "Tüm Aktiviteler" görünümünde bile hiç listelenmemeli.
const HIDDEN_JOB_TYPES = new Set(["training-aspect-conversion"]);

const JOB_TYPE_TO_MEDIA = (() => {
  const map = {};
  for (const [key, value] of Object.entries(MEDIA_PRESETS)) {
    for (const jt of value.jobTypes) {
      map[jt] = key;
    }
  }
  map["image-editing"] = "image";
  map["image-animation"] = "image";
  return map;
})();

const renderPreview = (mediaType, resultUrl, alt, FallbackIcon, isPlaying) => {
  if (!resultUrl) return <FallbackIcon />;
  if (mediaType === "video" || mediaType === "avatar") {
    return (
      <>
        <div className="add-image-modal__history-thumb-video-bg" aria-hidden="true" />
        <LuPlay className="add-image-modal__history-thumb-play" />
      </>
    );
  }
  if (mediaType === "audio") {
    return (
      <>
        <div className="add-image-modal__history-thumb-video-bg" aria-hidden="true" />
        {isPlaying ? (
          <LuPause className="add-image-modal__history-thumb-play" />
        ) : (
          <LuPlay className="add-image-modal__history-thumb-play" />
        )}
      </>
    );
  }
  return (
    <>
      <img src={resultUrl} alt={alt || ""} />
      <LuEye className="add-image-modal__history-thumb-view" />
    </>
  );
};

const HistoryTab = ({
  onAddToScene,
  mediaType,
  mediaTypes,
  jobType,
  jobTypes,
  spendingLabel,
  showName = false,
  hideAddButton = false,
  compactPrompt = false,
}) => {
  const explicitJobTypes = Array.isArray(jobTypes) && jobTypes.length > 0 ? jobTypes : null;
  const typeList = Array.isArray(mediaTypes) && mediaTypes.length > 0 ? mediaTypes : null;
  const singleType = typeList ? null : mediaType || "image";
  const primaryPreset = typeList
    ? MEDIA_PRESETS[typeList[0]] || MEDIA_PRESETS.image
    : MEDIA_PRESETS[singleType] || MEDIA_PRESETS.image;

  const effectiveJobTypes = explicitJobTypes
    ? explicitJobTypes
    : typeList
      ? typeList.flatMap((t) => MEDIA_PRESETS[t]?.jobTypes || [])
      : jobType
        ? [jobType]
        : primaryPreset.jobTypes;
  const jobTypesKey = effectiveJobTypes.join(",");

  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoveredId, setHoveredId] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showAllTypes, setShowAllTypes] = useState(false);
  // An "add to scene" that came back as a failure (async adds only).
  const [addError, setAddError] = useState(null);

  const locale = i18n.language || "tr";

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const organizationId = getOrganizationId();
        if (!organizationId) {
          setItems([]);
          setError(i18n.t("ImageHistory.NoOrganization") || "Organizasyon bulunamadı");
          return;
        }
        const trainingId = window.idealStudioTrainingId;
        const authHeaders = { Authorization: `Bearer ${getToken()}` };

        const params = new URLSearchParams({
          page: "1",
          limit: "20",
        });
        if (!showAllTypes) {
          params.set("jobType", effectiveJobTypes.join(","));
        }
        if (trainingId) {
          params.set("trainingId", trainingId);
        }
        const url = `${process.env.MW_API_URL}api/organizations/${organizationId}/transactions?${params.toString()}`;
        const profileUrl = `${process.env.MW_API_URL}api/auth/profile`;

        const [response, profileResponse] = await Promise.all([
          fetch(url, { headers: authHeaders }),
          fetch(profileUrl, { headers: authHeaders }),
        ]);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (cancelled) return;
        const rawItems = Array.isArray(data?.items) ? data.items : [];
        setItems(rawItems.filter((it) => !HIDDEN_JOB_TYPES.has(it?.job?.type)));

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          if (cancelled) return;
          // availableCredits: the org balance narrowed by the user's team
          // budget. monthlyMinutesLimit (org balance) for older middlewares.
          const profileUser = profileData?.user ?? profileData?.data?.user;
          const limit = profileUser?.availableCredits ?? profileUser?.monthlyMinutesLimit;
          setBalance(limit != null && !Number.isNaN(Number(limit)) ? Number(limit) : null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(i18n.t("ImageHistory.LoadError") || "Geçmiş yüklenemedi");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [jobTypesKey, showAllTypes]);

  const totalSpending = useMemo(
    () =>
      items
        .filter((tx) => tx.type === "debit")
        .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0),
    [items]
  );

  const resolveItemMedia = useCallback(
    (item) => {
      const mt = JOB_TYPE_TO_MEDIA[item?.job?.type];
      if (mt) return MEDIA_PRESETS[mt] || primaryPreset;
      return primaryPreset;
    },
    [primaryPreset]
  );

  const resolveItemMediaType = useCallback(
    (item) => JOB_TYPE_TO_MEDIA[item?.job?.type] || singleType || "image",
    [singleType]
  );

  const addItemToScene = useCallback(
    (item) => {
      const job = item?.job;
      if (!onAddToScene || !canAddRow(job)) return;
      const url = rowResultUrl(job);
      const itemPreset = resolveItemMedia(item);
      const name = job?.name || resolveDefaultName(itemPreset);
      // Geçmişten sahneye eklenen sonuç henüz onaylanmamışsa widget'ta
      // "Onay Bekliyor" kalmasın.
      const approve = () => {
        if (job?.id && !job.approved) approveJob(job.id);
      };
      setAddError(null);
      // 4th arg: the job row — talking-avatar re-applies itself from it.
      const outcome = onAddToScene(url, name, resolveItemMediaType(item), job);
      if (!outcome || typeof outcome.then !== "function") {
        approve();
        return;
      }
      // Async add (talking avatar): approve only once it went through. A
      // failure ({ ok: false, message }) stays on screen; ok:false without a
      // message (the same add already running) is not an error.
      outcome.then(
        (res) => {
          if (res && res.ok === false) {
            if (res.message) setAddError(res.message);
            return;
          }
          approve();
        },
        () => setAddError(addFailedText())
      );
    },
    [onAddToScene, resolveItemMedia, resolveItemMediaType]
  );

  const handleRowDoubleClick = useCallback((item) => addItemToScene(item), [addItemToScene]);

  const handleAddClick = useCallback(
    (e, item) => {
      e.stopPropagation();
      addItemToScene(item);
    },
    [addItemToScene]
  );

  const creditUnit = i18n.t("ImageHistory.Credits") || "Kredi";
  const resolvedSpendingLabel =
    spendingLabel ||
    (typeList
      ? i18n.t("ImageHistory.TrainingSpending") || "Toplam Harcama"
      : i18n.t(primaryPreset.spendingLabelKey) || primaryPreset.spendingLabelFallback);
  const emptyLabel = typeList
    ? i18n.t("ImageHistory.EmptyAny") || "Henüz oluşturulmamış"
    : i18n.t(primaryPreset.emptyLabelKey) || primaryPreset.emptyLabelFallback;
  const promptColumnLabel =
    i18n.t(primaryPreset.promptLabelKey) || primaryPreset.promptLabelFallback;

  return (
    <div className="add-image-modal__history">
      <div className="add-image-modal__history-summary">
        <div className="add-image-modal__history-summary-item">
          <LuWallet />
          <span className="add-image-modal__history-summary-label">
            {i18n.t("ImageHistory.CurrentBalance") || "Mevcut Bakiye"}:
          </span>
          <span className="add-image-modal__history-summary-value">
            {balance !== null ? balance.toFixed(2) : "—"}
          </span>
        </div>
        <span className="add-image-modal__history-summary-divider">|</span>
        <div className="add-image-modal__history-summary-item">
          <LuClock />
          <span className="add-image-modal__history-summary-label">{resolvedSpendingLabel}:</span>
          <span className="add-image-modal__history-summary-value">{totalSpending.toFixed(2)}</span>
        </div>
        <button
          type="button"
          className={`add-image-modal__history-toggle-all${
            showAllTypes ? " add-image-modal__history-toggle-all--active" : ""
          }`}
          onClick={() => setShowAllTypes((v) => !v)}
        >
          {i18n.t("ImageHistory.ShowAll") || "Tüm Aktiviteler"}
        </button>
      </div>

      {addError && (
        <div
          role="alert"
          style={{
            margin: "8px 0",
            padding: "8px 12px",
            borderRadius: 8,
            background: "#fee2e2",
            color: "#991b1b",
            fontSize: 13,
          }}
        >
          {addError}
        </div>
      )}

      {playingAudio && (
        <audio
          key={playingAudio.id}
          src={playingAudio.url}
          autoPlay
          onEnded={() => setPlayingAudio(null)}
          style={{ display: "none" }}
        />
      )}

      {previewMedia && (
        <div className="add-image-modal__history-player" onClick={() => setPreviewMedia(null)}>
          <div
            className="add-image-modal__history-player-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="add-image-modal__history-player-close"
              onClick={() => setPreviewMedia(null)}
              title={i18n.t("ImageHistory.Close") || "Kapat"}
            >
              <LuX />
            </button>
            {previewMedia.type === "image" ? (
              <img
                src={previewMedia.url}
                alt=""
                className="add-image-modal__history-player-video"
              />
            ) : (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="add-image-modal__history-player-video"
              />
            )}
          </div>
        </div>
      )}

      <div className="add-image-modal__history-table-wrap">
        <table className="add-image-modal__history-table">
          <thead>
            <tr>
              <th>{i18n.t("ImageHistory.Preview") || "Preview"}</th>
              {typeList && <th>{i18n.t("ImageHistory.Category") || "Tür"}</th>}
              {showName && <th>{i18n.t("ImageHistory.Name") || "İsim"}</th>}
              <th>{i18n.t("ImageHistory.Model") || "Model"}</th>
              <th>{i18n.t("ImageHistory.DateTime") || "Date & Time"}</th>
              <th>{promptColumnLabel}</th>
              <th>{i18n.t("ImageHistory.Status") || "Status"}</th>
              <th>{i18n.t("ImageHistory.Amount") || "Amount"}</th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 6 }).map((_, idx) => (
                <tr key={`sk-${idx}`} className="add-image-modal__history-row--skeleton">
                  <td>
                    <div className="add-image-modal__history-thumb add-image-modal__skeleton-thumb" />
                  </td>
                  <td colSpan={(typeList ? 6 : 5) + (showName ? 1 : 0)}>
                    <div className="add-image-modal__skeleton-name" />
                  </td>
                </tr>
              ))}

            {!loading && error && (
              <tr>
                <td
                  colSpan={(typeList ? 7 : 6) + (showName ? 1 : 0)}
                  className="add-image-modal__history-empty"
                >
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && items.length === 0 && (
              <tr>
                <td
                  colSpan={(typeList ? 7 : 6) + (showName ? 1 : 0)}
                  className="add-image-modal__history-empty"
                >
                  {emptyLabel}
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              items.map((item) => {
                const job = item.job || {};
                const resultUrl = rowResultUrl(job);
                const status = job.status || "—";
                const isCompleted = isRowCompleted(job);
                const canAdd = canAddRow(job);
                const itemPreset = resolveItemMedia(item);
                const itemMediaType = JOB_TYPE_TO_MEDIA[job.type] || singleType || "image";
                const isVideoPlayable =
                  (itemMediaType === "video" || itemMediaType === "avatar") && !!resultUrl;
                const isAudioPlayable = itemMediaType === "audio" && !!resultUrl;
                const isImagePreviewable = itemMediaType === "image" && !!resultUrl;
                const isPlayable = isVideoPlayable || isAudioPlayable || isImagePreviewable;
                const promptText = extractPrompt(job, itemMediaType, locale);
                const promptLimit = compactPrompt ? 50 : PROMPT_LIMITS[itemMediaType] || 50;
                return (
                  <tr
                    key={item.id}
                    className={`add-image-modal__history-row${
                      hoveredId === item.id ? " add-image-modal__history-row--hover" : ""
                    }`}
                    onMouseEnter={() => setHoveredId(item.id)}
                    onMouseLeave={() =>
                      setHoveredId((current) => (current === item.id ? null : current))
                    }
                    onDoubleClick={() => handleRowDoubleClick(item)}
                  >
                    <td className="add-image-modal__history-cell add-image-modal__history-cell--preview">
                      <div
                        className={`add-image-modal__history-thumb${
                          isPlayable && !isImagePreviewable
                            ? " add-image-modal__history-thumb--video"
                            : ""
                        }${isImagePreviewable ? " add-image-modal__history-thumb--clickable" : ""}`}
                        onClick={
                          isPlayable
                            ? (e) => {
                                e.stopPropagation();
                                if (isAudioPlayable) {
                                  if (playingAudio?.id === item.id) {
                                    setPlayingAudio(null);
                                  } else {
                                    setPlayingAudio({ id: item.id, url: resultUrl });
                                  }
                                } else {
                                  setPlayingAudio(null);
                                  setPreviewMedia({
                                    url: resultUrl,
                                    type: isImagePreviewable ? "image" : "video",
                                  });
                                }
                              }
                            : undefined
                        }
                        role={isPlayable ? "button" : undefined}
                        title={
                          isImagePreviewable
                            ? i18n.t("ImageHistory.View") || "Görüntüle"
                            : isPlayable
                              ? i18n.t("ImageHistory.Play") || "Oynat"
                              : undefined
                        }
                      >
                        {renderPreview(
                          itemMediaType,
                          resultUrl,
                          job.name,
                          itemPreset.fallbackIcon,
                          isAudioPlayable && playingAudio?.id === item.id
                        )}
                        {canAdd &&
                          hoveredId === item.id &&
                          itemMediaType !== "audio" &&
                          !hideAddButton && (
                            <button
                              type="button"
                              className="add-image-modal__history-add-btn"
                              onClick={(e) => handleAddClick(e, item)}
                              title={i18n.t("AddToScene") || "Sahneye ekle"}
                            >
                              +
                            </button>
                          )}
                      </div>
                    </td>
                    {typeList && (
                      <td className="add-image-modal__history-cell">
                        {JOB_TYPE_TO_MEDIA[job.type] ? (
                          <span className="add-image-modal__history-category">
                            {i18n.t(itemPreset.categoryLabelKey) ||
                              itemPreset.categoryLabelFallback}
                          </span>
                        ) : null}
                      </td>
                    )}
                    {showName && (
                      <td className="add-image-modal__history-cell" title={localizeName(job.name)}>
                        {localizeName(job.name) || "—"}
                      </td>
                    )}
                    <td className="add-image-modal__history-cell">
                      {displayModel(job.model) || "—"}
                    </td>
                    <td className="add-image-modal__history-cell">
                      {formatDateTime(item.createdAt, locale)}
                    </td>
                    <td
                      className={`add-image-modal__history-cell add-image-modal__history-cell--prompt${
                        !compactPrompt && (itemMediaType === "video" || itemMediaType === "image")
                          ? " add-image-modal__history-cell--prompt-wide"
                          : ""
                      }`}
                      title={promptText}
                    >
                      {truncate(promptText, promptLimit)}
                    </td>
                    <td className="add-image-modal__history-cell">
                      <span
                        className={`add-image-modal__history-status${
                          isCompleted ? " add-image-modal__history-status--ok" : ""
                        }`}
                      >
                        <LuCheck />
                        <span>
                          {isCompleted ? i18n.t("ImageHistory.Completed") || "Completed" : status}
                        </span>
                      </span>
                    </td>
                    <td className="add-image-modal__history-cell add-image-modal__history-cell--amount">
                      -{(Number(item.amount) || 0).toFixed(2)} {creditUnit}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoryTab;
